// hooks/useForm.js
import { useState, useCallback } from 'react'

// O `fieldDefinitions` é o array de seções e campos, como `unitAssociationFields`
const useForm = (initialData, fieldDefinitions = []) => {
  const [formData, setFormData] = useState(initialData)
  const [initialFormData, setInitialFormData] = useState(initialData)

  const handleChange = useCallback(
    (fieldId, value) => {
      // ACEITA NULO/UNDEFINED: só processa se fieldId for string válida
      if (!fieldId || typeof fieldId !== 'string') {
        // Se for nulo/undefined, apenas ignora silenciosamente ou loga para debug
        if (process.env.NODE_ENV === 'development') {
          console.warn('handleChange: fieldId inválido ignorado', {
            fieldId,
            value,
            type: typeof fieldId,
          })
        }
        return //  Sai sem quebrar
      }

      let processedValue = value

      // Tenta encontrar a configuração do campo no fieldDefinitions
      const fieldConfig = fieldDefinitions
        .flatMap((section) => section.fields ?? []) // evita undefined se fields faltar
        .filter((field) => field != null && typeof field.id === 'string')
        .find((field) => field.id === fieldId)

      if (fieldConfig) {
        if (fieldConfig.isMulti) {
          processedValue = Array.isArray(value)
            ? value.map((item) => {
                // Garante que temos objetos { value, label }
                if (
                  typeof item === 'object' &&
                  'value' in item &&
                  'label' in item
                ) {
                  return item
                }
                return { value: item, label: String(item) } // fallback se necessário
              })
            : []
        } else if (
          fieldConfig.entity ||
          (fieldConfig.type && fieldConfig.type.includes('select'))
        ) {
          if (value === null) {
            processedValue = null
          } else if (
            typeof value === 'object' &&
            'value' in value &&
            'label' in value
          ) {
            processedValue = value
          } else {
            processedValue = { value, label: String(value) }
          }
        }
      } else {
        // Fallback defensivo quando fieldConfig não for encontrado
        if (
          Array.isArray(value) &&
          value.length &&
          value[0]?.value !== undefined
        ) {
          processedValue = value.map((item) => item.value)
        } else if (value && typeof value === 'object' && 'value' in value) {
          processedValue = value.value
        } else if (value === null) {
          processedValue = null
        }
      }

      // Atualiza o estado do formData com suporte a campos aninhados e arrays
      setFormData((prev) => {
        const newFormData = { ...prev }
        let currentLevel = newFormData

        const parts = fieldId.split('.')

        for (let i = 0; i < parts.length; i++) {
          let part = parts[i]
          const arrayMatch = part.match(/(\w+)\[(\d+)\]/)

          if (arrayMatch) {
            const arrayName = arrayMatch[1]
            const index = parseInt(arrayMatch[2], 10)

            if (
              !currentLevel[arrayName] ||
              !Array.isArray(currentLevel[arrayName])
            ) {
              currentLevel[arrayName] = []
            }

            if (currentLevel[arrayName].length <= index) {
              currentLevel[arrayName] = [
                ...currentLevel[arrayName],
                ...Array(index - currentLevel[arrayName].length + 1).fill(null),
              ]
            }

            if (i === parts.length - 1) {
              currentLevel[arrayName][index] = processedValue
            } else {
              currentLevel[arrayName][index] = {
                ...(currentLevel[arrayName][index] || {}),
              }
              currentLevel = currentLevel[arrayName][index]
            }
          } else {
            if (i === parts.length - 1) {
              currentLevel[part] = processedValue
            } else {
              currentLevel[part] = { ...(currentLevel[part] || {}) }
              currentLevel = currentLevel[part]
            }
          }
        }

        return newFormData
      })
    },
    [fieldDefinitions]
  )

  const resetForm = useCallback(
    (preserveFields = []) => {
      setFormData((currentData) => {
        // Reset normal
        const newData = structuredClone
          ? structuredClone(initialData)
          : JSON.parse(JSON.stringify(initialData))

        preserveFields.forEach((fieldId) => {
          const parts = fieldId.split('.')

          // Obtém valor atual
          let currentValue = currentData
          for (const part of parts) {
            currentValue = currentValue?.[part]

            if (currentValue === undefined) {
              return
            }
          }

          // Seta valor preservado no objeto resetado
          let target = newData

          for (let i = 0; i < parts.length - 1; i++) {
            const part = parts[i]

            if (!target[part]) {
              target[part] = {}
            }

            target = target[part]
          }

          target[parts[parts.length - 1]] = currentValue
        })

        return newData
      })
    },
    [initialData]
  )

  const initializeData = useCallback((fields) => {
    const initializedData = fields.reduce((acc, section) => {
      return section.fields.reduce((innerAcc, field) => {
        // Validação: só processa se field.id for string
        if (!field?.id || typeof field.id !== 'string') {
          return innerAcc
        }

        const parts = field.id.split('.')
        if (parts.length > 1) {
          const category = parts[0]
          const key = parts.slice(1).join('.')
          return {
            ...innerAcc,
            [category]: {
              ...innerAcc[category],
              [key]: null,
            },
          }
        }
        return { ...innerAcc, [field.id]: null }
      }, acc)
    }, {})
    setFormData(initializedData)
    setInitialFormData(initializedData)
  }, [])

  // O `formatData` é usado para preencher o formulário com dados da API.
  // Ele deve garantir que os IDs recebidos da API sejam armazenados como IDs no formData.
  // A conversão para {value, label} para exibição é responsabilidade de `getSelectedValue` no componente da página.
  const formatData = useCallback((response, fields) => {
    const parsedData = {}

    fields.forEach((section) => {
      section.fields.forEach((field) => {
        // Validação de segurança
        if (!field?.id || typeof field.id !== 'string') {
          return // pula campo inválido
        }

        const fieldName = field.id
        const parts = fieldName.split('.')

        let valueFromResponse
        if (parts.length > 1) {
          const category = parts[0]
          const key = parts.slice(1).join('.')
          valueFromResponse = response[category]?.[key]
        } else {
          valueFromResponse = response[fieldName]
        }

        let processedValue =
          valueFromResponse !== undefined && valueFromResponse !== null
            ? valueFromResponse
            : null

        // Se o campo é um multi-select e a API retorna um array (de IDs, como `attachedUnitsData.map` indica)
        if (field.isMulti && Array.isArray(processedValue)) {
          // Armazena como array de IDs (não converte para {value, label} aqui)
          // eslint-disable-next-line no-self-assign
          processedValue = processedValue
        }
        // Se o campo é um select simples e a API retorna um objeto (e.g., {id: 1, name: "Unit"})
        else if (
          !field.isMulti &&
          processedValue !== null &&
          typeof processedValue === 'object' &&
          'id' in processedValue
        ) {
          // Armazena apenas o ID
          processedValue = processedValue.id
        }
        // Para outros casos, o `processedValue` permanece como veio da resposta da API.

        if (parts.length > 1) {
          const category = parts[0]
          const key = parts.slice(1).join('.')
          parsedData[category] = {
            ...(parsedData[category] || {}),
            [key]: processedValue,
          }
        } else {
          parsedData[fieldName] = processedValue
        }
      })
    })

    setFormData((prev) => ({ ...prev, ...parsedData }))
  }, [])

  return {
    formData,
    initialFormData,
    setFormData,
    handleChange,
    resetForm,
    initializeData,
    formatData,
  }
}

export default useForm
